import { useCallback, useRef, useState } from 'react';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';

const COUNTS = ['plates', 'slants', 'banked_slants', 'dna', 'runs', 'identifications'];

/**
 * What a row and its dossier must agree on: the five stages' details and the
 * record counts. GET /specimens/overview and /specimens/{id}/dossier compute
 * both the same way (specimen_overview._item), so they differ only when one
 * of them was read before something changed
 * (test_a_row_and_its_dossier_tell_the_same_story).
 */
export function summaryOf(record) {
  if (!record) return '';
  const pipeline = record.pipeline || {};
  const stages = Object.keys(pipeline).sort().map((stage) => `${stage}:${pipeline[stage]?.detail ?? '-'}`);
  const counts = record.counts || {};
  return `${stages.join(',')}|${COUNTS.map((key) => counts[key] ?? '').join(',')}`;
}

/**
 * Everything about one isolate, read once (GET /specimens/{id}/dossier):
 * an expanded row, its tabs and the quick-action drawer all read the same
 * copy. Opening a row again, or another tab, does not ask the server again;
 * `refresh` does, after an action changed it, and `reconcile` does when the
 * table was read again and a row no longer tells what its dossier does --
 * DNA imported in another tab, another person's check, an intake edited here
 * -- so a dialog never says "no DNA extracted" under a row that shows it.
 *
 * Each entry is { data, loading, error }.
 */
export default function useDossiers() {
  const [entries, setEntries] = useState({});
  const asked = useRef(new Set());
  const current = useRef(entries);
  current.current = entries;
  // id -> the row summary a re-read was asked for: one re-read per change,
  // never a loop if the two keep disagreeing.
  const reconciled = useRef(new Map());

  const fetchOne = useCallback((id) => {
    setEntries((all) => ({ ...all, [id]: { data: all[id]?.data || null, loading: true, error: '' } }));
    return api.get(`/specimens/${id}/dossier`)
      .then((res) => setEntries((all) => ({ ...all, [id]: { data: res?.data || null, loading: false, error: '' } })))
      .catch((e) => setEntries((all) => ({
        ...all, [id]: { data: all[id]?.data || null, loading: false, error: errorText(e) },
      })));
  }, []);

  /** Read it unless it has been read (or is being read) already. */
  const ensure = useCallback((id) => {
    if (!id || asked.current.has(id)) return;
    asked.current.add(id);
    fetchOne(id);
  }, [fetchOne]);

  /** Read it again: an action changed it. Only one already read is re-read. */
  const refresh = useCallback((id) => {
    if (!id || !asked.current.has(id)) return Promise.resolve();
    return fetchOne(id);
  }, [fetchOne]);

  /** The table was read again: re-read every dossier already read whose row
   *  now tells another story. One being read is left to arrive. */
  const reconcile = useCallback((items) => {
    (items || []).forEach((item) => {
      const id = item?.specimen_id;
      const entry = id && asked.current.has(id) ? current.current[id] : null;
      if (!entry?.data || entry.loading) return;
      const row = summaryOf(item);
      if (row === summaryOf(entry.data) || reconciled.current.get(id) === row) return;
      reconciled.current.set(id, row);
      fetchOne(id);
    });
  }, [fetchOne]);

  const get = useCallback((id) => entries[id] || { data: null, loading: false, error: '' }, [entries]);

  return { get, ensure, refresh, reconcile };
}
